/**
 * The `servicenow_incident` agent-action lifecycle capability — the first
 * NON-graph permission-gated AI action. Wires the `create_servicenow_incident`
 * tool's SSE events, the agent-writes apply/reject endpoints (shared with
 * cypher writes — they key off `tool_use_id` and dispatch on `actionKind`
 * server-side), and the per-row resume text into the generic registry.
 *
 * Importing this module registers the capability as a side effect (the state
 * barrel pulls it in), so routing/resume/panel code only ever talks to the
 * registry, never to ServiceNow specifics.
 */
import { agentWritesApi } from '../../api/agents/agent-writes'
import type {
  InFlightAgentActionProposal,
  InFlightServicenowIncidentProposal
} from '../../slices/agent-orchestrator-slice'
import {
  buildServicenowIncidentEntryFromToolInput,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

const SERVICENOW_INCIDENT_TOOL_NAME = 'create_servicenow_incident'

function readString(record: Record<string, unknown>, key: string): string | null {
  const v = record[key]
  return typeof v === 'string' ? v : null
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const v = record[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/**
 * Parse the `create_servicenow_incident` propose output when it carries a
 * legacy `pending: true` envelope (not-yet-upgraded server during rollout).
 * Returns the canonical id hydration, or `null` if the tool didn't return
 * a `pending: true` envelope (validation/persistence failure). Blocking
 * (settled) outputs are handled by `parseSettledToolComplete` first.
 */
function readProposeOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const obj = output as Record<string, unknown>
  if (obj.pending !== true) return null
  const planId = readString(obj, 'plan_id')
  if (planId === null) return null
  const rawResults = obj.results
  if (!Array.isArray(rawResults)) return null
  const results: Array<{ toolUseId: string; writeId: string; index?: number }> = []
  for (const raw of rawResults) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const r = raw as Record<string, unknown>
    const toolUseId = readString(r, 'tool_use_id')
    const writeId = readString(r, 'write_id')
    if (toolUseId === null || writeId === null) continue
    results.push({ toolUseId, writeId, index: readNumber(r, 'index') })
  }
  return { parentToolUseId, planId, results }
}

/** Compact identifier text for the created incident in the resume instruction. */
function createdIncidentRef(
  created: InFlightServicenowIncidentProposal['createdIncident']
): string {
  if (!created) return ''
  const parts: string[] = []
  if (created.number) parts.push(created.number)
  if (created.sysId) parts.push(`sys_id ${created.sysId}`)
  return parts.join(', ')
}

/**
 * Strip double quotes from the summary so it survives the shared
 * `ResumeDecisionsBubble` parser, whose row regex captures the summary as
 * `"([^"]*)"` and would break on a nested quote.
 */
function sanitizeSummary(text: string): string {
  return text.replace(/"/g, '').trim()
}

export const servicenowIncidentLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'servicenow_incident',
  toolName: SERVICENOW_INCIDENT_TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    const entry = buildServicenowIncidentEntryFromToolInput(toolUseId, input, startedAtMs)
    return entry ? [entry] : []
  },

  parseToolComplete(toolUseId, output) {
    return readProposeOutput(toolUseId, output)
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return async (dispatch): Promise<AgentActionApplyOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({
            toolUseId,
            graphAffecting: false
          })
        ).unwrap()
        if (result.ok) {
          const created = result.result ?? {}
          const sysId = typeof created.sys_id === 'string' ? created.sys_id : undefined
          const number = typeof created.number === 'string' ? created.number : undefined
          return {
            kind: 'success',
            settledMeta: { createdIncident: { sysId, number } }
          }
        }
        // ServiceNow create failed upstream. The server has already moved
        // the row to `state: 'error'`; treat the card as terminal so the
        // plan's resume instruction surfaces the failure to the agent.
        return { kind: 'apply_failed', error: result.error ?? 'Create incident failed.' }
      } catch (err) {
        // HTTP 409 with `{ ok: false, error }` is a structured refusal,
        // not a transport blip — settle as terminal.
        const refused = readStructuredMutationError(err)
        if (refused !== null) {
          return { kind: 'apply_failed', error: refused.error }
        }
        const message = err instanceof Error ? err.message : 'Create incident failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  reject(toolUseId, reason) {
    return async (dispatch): Promise<AgentActionRejectOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        if (result.ok) {
          return { kind: 'success', settledMeta: { rejectionReason: result.reason ?? reason } }
        }
        return { kind: 'reject_failed', error: result.error ?? 'Reject failed.' }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Reject failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  // Uses the canonical verbs the shared `ResumeDecisionsBubble` parser
  // recognizes (APPLIED / APPLY_FAILED / REJECTED / UNDECIDED). The
  // created-incident identifiers ride in the `;` tail so they surface in
  // the expandable detail row rather than breaking the row regex.
  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    if (proposal.kind !== 'servicenow_incident') {
      return `${displayNumber}. UNDECIDED — "${sanitizeSummary(proposal.summary)}".`
    }
    const summary = sanitizeSummary(proposal.summary || proposal.shortDescription)
    const terminal = proposal.terminalState
    if (terminal?.kind === 'applied') {
      const ref = createdIncidentRef(proposal.createdIncident)
      const tail = ref ? `; created ${ref}` : '; incident created'
      return `${displayNumber}. APPLIED — "${summary}"${tail}.`
    }
    if (terminal?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${terminal.error}.`
    }
    if (terminal?.kind === 'rejected') {
      const reason = proposal.rejectionReason || 'rejected by user'
      return `${displayNumber}. REJECTED — "${summary}"; reason: "${reason}".`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

registerAgentActionLifecycle(servicenowIncidentLifecycleCapability)
